import * as amplitude from '@amplitude/analytics-browser'
import type { UserSession } from '@/domains/auth/AuthenticationPort'
import { AnswerType } from '@/domains/AnswerType'

export type START_DIAGNOSIS_SOURCE = 'my-hub' | 'priorities'

export type CONTINUE_ACTION_SOURCE =
  'my-hub' | 'my-team/vendor' | 'my-team/diagnoses' | 'priorities'

export type EventPropertiesMap = {
  APP_OPENED: undefined
  APP_CLOSED: undefined
  START_DIAGNOSIS: {
    diagnosisId: string
    familyId: number
    isPriorityFamily: boolean
    source: START_DIAGNOSIS_SOURCE
  }
  END_DIAGNOSIS: {
    diagnosisId: string
  }
  START_QUESTION: {
    diagnosisId: string
    familyId: number
    questionId: string
  }
  END_QUESTION: {
    diagnosisId: string
    familyId: number
    questionId: string
  }
  START_SECTION: undefined
  END_SECTION: undefined
  HISTORY_TAB_CLICK: undefined
  ACTION_TAB_CLICK: undefined
  HELP_BUTTON_CLICK: { questionId: string }
  MY_TEAM_BUTTON_CLICK: undefined
  PRIORITY_BUTTON_CLICK: undefined
  MY_TEAM_ALL_TAB_CLICK: undefined
  ANSWER_QUESTION: {
    diagnosisId: string
    familyId: number
    questionId: string
    answerValue: AnswerType
  }
  UPDATE_ANSWER: {
    familyId: number
    questionId: string
  }
  QUESTION_BACK_BUTTON_CLICK: { familyId: number; questionId: string }
  BROWSER_BACK_BUTTON_CLICK: undefined
  CONTINUE_DIAGNOSIS_BUTTON_CLICK: { diagnosisId: string }
  CONTINUE_ACTION_PLAN_BUTTON_CLICK: { diagnosisId: string; source: CONTINUE_ACTION_SOURCE }
  LOGOUT_BUTTON_CLICK: undefined
  REPORT_BUG_BUTTON_CLICK: undefined
  TEAM_CHANGE: undefined
  ACTION_CHECKED: {
    actionPlanId: string
    actionId: string
  }
  ACTION_UNCHECKED: {
    actionPlanId: string
    actionId: string
  }
  DAO_BUTTON_CLICK: {
    diagnosisId: string
    questionId: string
  }
  DAO_OPEN: {
    diagnosisId: string
    questionId: string
  }
}

export type FCSAmplitudeEvent = keyof EventPropertiesMap

// Custom logger to filter out noisy network errors from Amplitude
class SilentNetworkLogger implements amplitude.Types.ILogger {
  logLevel = amplitude.Types.LogLevel.Warn

  disable(): void {
    this.logLevel = amplitude.Types.LogLevel.None
  }

  enable(logLevel: amplitude.Types.LogLevel): void {
    this.logLevel = logLevel
  }

  private formatLog(level: string, args: any[]): string {
    return `[Amplitude] [${level}] ${args.map(String).join(' ')}`
  }

  debug(...args: any[]): void {
    if (this.logLevel < amplitude.Types.LogLevel.Verbose) return
    console.debug(this.formatLog('Debug', args))
  }

  log(...args: any[]): void {
    if (this.logLevel < amplitude.Types.LogLevel.Verbose) return
    console.log(this.formatLog('Log', args))
  }

  warn(...args: any[]): void {
    if (this.logLevel < amplitude.Types.LogLevel.Warn) return
    if (this.isNetworkOrRetryError(args)) return
    console.warn(this.formatLog('Warn', args))
  }

  error(...args: any[]): void {
    if (this.logLevel < amplitude.Types.LogLevel.Error) return
    if (this.isNetworkOrRetryError(args)) return
    console.error(this.formatLog('Error', args))
  }

  private isNetworkOrRetryError(args: any[]): boolean {
    const msg = args.map(String).join(' ')
    if (/Event rejected due to exceeded retry count/i.test(msg)) return true
    return (
      msg.includes('Failed to fetch') ||
      msg.includes('Fetch error') ||
      msg.includes('Network request failed')
    )
  }
}

let initialized = false

export const initAmplitude = (apiKey: string, environment: string): void => {
  if (environment === 'local' || environment === 'dev') return
  amplitude.init(apiKey, {
    serverZone: 'EU',
    minIdLength: 1,
    loggerProvider: new SilentNetworkLogger(),
    defaultTracking: {
      sessions: true,
      pageViews: false,
      formInteractions: false,
      fileDownloads: false,
    },
    autocapture: {
      elementInteractions: false,
      pageViews: false,
    },
  })
  initialized = true
}

export const identifyUser = (user: UserSession): void => {
  if (!initialized) return

  amplitude.setUserId(user.id)

  const identifyEvent = new amplitude.Identify()
  if (user.email) identifyEvent.set('email', user.email)
  if (user.name) identifyEvent.set('name', user.name)
  amplitude.identify(identifyEvent)
}

export const trackEvent = <T extends FCSAmplitudeEvent>(
  eventName: T,
  eventProperties?: EventPropertiesMap[T]
): void => {
  if (!initialized) return
  amplitude.track(eventName, eventProperties)
}

export const trackAndFlushEvent = <T extends FCSAmplitudeEvent>(
  eventName: T,
  eventProperties?: EventPropertiesMap[T]
): Promise<void> => {
  trackEvent(eventName, eventProperties)
  return flushEvents()
}

const flushEvents = (): Promise<void> => {
  if (!initialized) return Promise.resolve()
  return amplitude.flush().promise
}
